import {
  getPrimaryColor,
  getPrimaryOpaqueColor,
  Icon,
  INSURANCE_TYPE,
  LockIcon,
  Styles,
} from '@orcar/common-d2c';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { getInsuranceName } from '@/utils/insurance';
import TableBodyRow from './TableBodyRow';
import { OrderConfirmationProps } from '../orderConfirmation/OrderConfirmationPage';

const app = import.meta.env.VITE_PACKAGE_NAME;

interface InsuranceTableProps extends OrderConfirmationProps {
  addInsurance?: boolean;
  insurance: string;
  companyName: string;
  canAddUnlimitedOption: boolean;
  coverageLimit: string;
  deductible: string;
  lossOfUseFee: string;
}

const InsuranceTable = ({
  addInsurance,
  insurance,
  isMobile,
  companyName,
  canAddUnlimitedOption,
  coverageLimit,
  deductible,
  lossOfUseFee,
}: InsuranceTableProps) => {
  const [lockWidth, setLockWidth] = useState(0);

  const isSuper = insurance === INSURANCE_TYPE.SUPER;

  useEffect(() => {
    const width = document.getElementById('lockWidth')?.clientWidth;

    setLockWidth(width as number);
  }, [isMobile]);

  return (
    <Wrapper>
      {!canAddUnlimitedOption && !isSuper && (
        <Lock isMobile={isMobile} lockWidth={lockWidth}>
          <Icon
            src={LockIcon}
            width={26}
            height={32}
            iconcolor={Styles.SVG_FILTER.WHITE}
          />
          <p>해당 상품은 무제한특약 가입이 불가능합니다.</p>
        </Lock>
      )}
      <Table cellSpacing='0'>
        <colgroup>
          <col width={isMobile ? '90px' : '180px'} />
          <Column
            selected={!addInsurance}
            width={isMobile ? '115px' : '276px'}
            isCenter
          />
          <Column
            selected={addInsurance}
            width={isMobile ? '115px' : '276px'}
          />
        </colgroup>
        <thead>
          <tr>
            <TableHeader isMobile={isMobile}>보상 내용</TableHeader>
            <TableHeader selected={!addInsurance} isMobile={isMobile}>
              {getInsuranceName(insurance)}
            </TableHeader>
            <TableHeader
              id='lockWidth'
              selected={addInsurance}
              isMobile={isMobile}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '6px',
                }}
              >
                {isSuper
                  ? getInsuranceName(INSURANCE_TYPE.PERFECT)
                  : getInsuranceName(insurance) + ' + 무제한특약'}
              </span>
            </TableHeader>
          </tr>
        </thead>

        <tbody>
          <TableBodyRow
            insuranceType={insurance}
            addInsurance={addInsurance}
            companyName={companyName}
            value={deductible}
          >
            면책금
          </TableBodyRow>

          <TableBodyRow
            insuranceType={insurance}
            addInsurance={addInsurance}
            companyName={companyName}
            value={coverageLimit}
          >
            보상 한도
          </TableBodyRow>

          <TableBodyRow
            insuranceType={insurance}
            addInsurance={addInsurance}
            companyName={companyName}
            hasTooltip
            value={lossOfUseFee}
          >
            휴차 보상료
          </TableBodyRow>

          <TableBodyRow
            insuranceType={insurance}
            addInsurance={addInsurance}
            companyName={companyName}
            hasTooltip
          >
            단독 사고
          </TableBodyRow>

          <TableBodyRow
            insuranceType={insurance}
            addInsurance={addInsurance}
            hasTooltip
            isBottom
          >
            휠/타이어
          </TableBodyRow>
        </tbody>
      </Table>
    </Wrapper>
  );
};

export { InsuranceTable };

const Wrapper = styled.div`
  position: relative;
`;

const Table = styled.table`
  position: relative;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-bottom: 12px;
  background-color: ${({ theme }) => theme.COLOR.GREY_OPAQUE};
`;

const TableHeader = styled.th<{ selected?: boolean; isMobile: boolean }>`
  text-align: left;
  height: ${({ isMobile }) => (isMobile ? '40px' : '50px')};
  padding: ${({ isMobile }) => (isMobile ? '0 8px' : '0 20px')};
  border-top: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  background-color: ${({ theme }) => theme.COLOR.GREY_BG};
  ${({ isMobile, theme }) => isMobile && theme.FONT.B2};
  font-weight: 600;

  ${({ selected, theme }) =>
    selected &&
    css`
      color: ${getPrimaryColor(app)};
      border: 1px solid ${getPrimaryColor(app)};
      border-bottom: 1px solid ${theme.COLOR.GREY_05};
      background-color: ${getPrimaryOpaqueColor(app, true)};
    `}
`;

const Column = styled.col<{
  selected?: boolean;
  isCenter?: boolean;
  isLock?: boolean;
}>`
  border-left: ${({ isCenter, theme }) =>
    isCenter && `1px solid ${theme.COLOR.GREY_05}`};

  ${({ selected, theme }) =>
    selected &&
    css`
      border: 1px solid ${getPrimaryColor(app)};
    `}
`;

const Lock = styled.div<{ isMobile: boolean; lockWidth: number }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  top: 0;
  right: 0;
  padding: ${({ isMobile }) => (isMobile ? '0 10px' : '0 20px')};
  background-color: ${({ theme }) => theme.COLOR.GREY_OPAQUE_02};
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_OPAQUE_02};
  width: ${({ lockWidth }) => `${lockWidth}px`};
  height: 100%;
  z-index: 1;

  & > p {
    margin-top: 20px;
    color: ${({ theme }) => theme.COLOR.WHITE};
    ${({ isMobile, theme }) => isMobile && theme.NEW_FONT.B2};
    line-height: 24px;
    font-weight: 600;
  }
`;
